import { useState, useRef } from 'react';
import { Shield, Camera, X, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import API_URL from '../config/api';

export default function VerificationModal({ isOpen, onClose, onVerified }) {
  const [selfie, setSelfie] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [verified, setVerified] = useState(false);
  const fileRef = useRef(null);

  if (!isOpen) return null;

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max 10MB');
      return;
    }
    setSelfie(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleVerify = async () => {
    if (!selfie) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', selfie);
      await axios.post(`${API_URL}/api/users/me/verify`, fd, { withCredentials: true });
      setVerified(true);
      toast.success('Profile verified!');
      if (onVerified) onVerified();
      setTimeout(() => {
        onClose();
        setVerified(false);
        setSelfie(null);
        setPreview(null);
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSkip = () => {
    onClose();
    setSelfie(null);
    setPreview(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleSkip} />

      <div className="relative bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden" data-testid="verification-modal">
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#F5F2EB] flex items-center justify-center text-[#6C6C67] hover:bg-[#E5E3DB] transition-colors z-10"
          data-testid="verification-close"
        >
          <X className="w-4 h-4" />
        </button>

        {verified ? (
          /* ── Success state ── */
          <div className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-[#2D6A4F]" />
            </div>
            <h3 className="text-xl font-serif font-medium text-[#2D2D2A] mb-2">You're Verified!</h3>
            <p className="text-[#6C6C67] text-sm">Your profile now shows a verified badge.</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-[#6E1A24] px-6 py-5 text-white text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center">
                <Shield className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-serif font-medium">Verify Your Profile</h3>
              <p className="text-white/70 text-sm mt-1">Verified profiles get more matches and build trust</p>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {preview ? (
                <div className="relative aspect-square rounded-2xl overflow-hidden bg-[#F5F2EB]">
                  <img src={preview} alt="Selfie preview" className="w-full h-full object-cover" />
                  <button
                    onClick={() => { setSelfie(null); setPreview(null); }}
                    className="absolute top-3 right-3 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                    data-testid="verification-remove-selfie"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  className="w-full aspect-square rounded-2xl border-2 border-dashed border-[#E5E3DB] flex flex-col items-center justify-center cursor-pointer hover:border-[#6E1A24] hover:bg-[#6E1A24]/5 transition-all"
                  data-testid="verification-upload-btn"
                >
                  <Camera className="w-10 h-10 text-[#6C6C67] mb-3" />
                  <p className="font-medium text-[#2D2D2A]">Take or upload a selfie</p>
                  <p className="text-sm text-[#6C6C67] mt-1">Clear face photo, good lighting</p>
                </button>
              )}

              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="user"
                onChange={handleFileSelect}
                className="hidden"
              />

              <div className="flex gap-3">
                <Button
                  onClick={handleSkip}
                  variant="outline"
                  className="rounded-2xl py-3 font-medium border-[#E5E3DB] text-[#6C6C67] hover:bg-[#F5F4F0] transition-all flex-1"
                  data-testid="verification-skip"
                >
                  Maybe Later
                </Button>
                <Button
                  onClick={handleVerify}
                  disabled={!selfie || uploading}
                  className="bg-[#6E1A24] text-white rounded-2xl py-3 font-semibold hover:bg-[#8A2331] transition-all flex items-center justify-center gap-2 disabled:opacity-50 flex-1"
                  data-testid="verification-submit"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Shield className="w-4 h-4" />
                  )}
                  Verify Me
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
